
import React, { useState } from 'react';
import { X, Check, ArrowRight } from 'lucide-react';
import axios from '../utils/axios';
import toast from 'react-hot-toast';

const MoveProductModal = ({
    isOpen,
    onClose,
    product,
    categories,
    onMoveSuccess
}) => {
    const [targetCategoryId, setTargetCategoryId] = useState('');
    const [loading, setLoading] = useState(false);

    if (!isOpen || !product) return null;

    const handleMove = async () => {
        if (!targetCategoryId) {
            toast.error('Please select a target category');
            return;
        }

        if (targetCategoryId === product.categoryId || targetCategoryId === product.category?._id || targetCategoryId === product.product?.categoryId) {
            toast.error('Product is already in this category');
            return;
        }

        setLoading(true);
        try {
            // We use the same update endpoint as editing a product
            // Construct the payload to only update the categoryId
            // We need to match the structure expected by PUT /admin/products/:id

            // The backend expects: 
            // { 
            //   product: { ...productData, categoryId: newId },
            //   supplier: { ...supplierData } 
            // }
            // However, looking at previous edits, we might need to check if there's a specialized endpoint or if we need to send full data.
            // Let's try sending just the categoryId update if the backend supports partial updates via PATCH, 
            // OR we might need to fetch the full product data first if we don't have it all.
            // Actually, usually PUT requires full data. 
            // Let's check if we have a specific 'move' endpoint for single product. 
            // We don't. We have bulk move. 
            // We should use the existing PUT /admin/products/:id endpoint.
            // But we need to be careful not to overwrite other fields with nulls if 'product' prop is incomplete.

            // Wait, looking at ProductManagement.js, 'product' object usually contains full data from the table.
            // But let's verify if we need to send EVERYTHING.
            // Implementation Plan said: "Calls PUT /admin/products/:id to update the category."

            // Verify ID availability
            const productId = product.id || product._id;
            console.log('Using Product ID for move:', productId);

            if (!productId) {
                throw new Error('Product ID is missing');
            }

            // Safe approach: Fetch fresh product data, modify category, then PUT.
            const { data: fullProduct } = await axios.get(`/admin/products/${productId}`);

            const payload = {
                ...fullProduct,
                product: {
                    ...fullProduct.product,
                    categoryId: targetCategoryId
                }
            };

            await axios.put(`/admin/products/${productId}`, payload);

            toast.success('Product moved successfully');
            onMoveSuccess();
            onClose();
        } catch (error) {
            console.error('Error moving product:', error);
            toast.error('Failed to move product');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg w-full max-w-md shadow-xl animate-in fade-in zoom-in duration-200">
                <div className="flex justify-between items-center p-4 border-b bg-[#0A5F38] rounded-t-lg">
                    <h3 className="text-lg font-bold text-white flex items-center">
                        <ArrowRight className="h-5 w-5 mr-2" />
                        Move Product
                    </h3>
                    <button onClick={onClose} className="text-white hover:text-gray-200">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-6">
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Product Name
                        </label>
                        <div className="p-2 bg-gray-50 rounded border text-gray-700">
                            {product.product?.name || product.name || 'Unknown Product'}
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Current Category
                        </label>
                        <div className="p-2 bg-gray-50 rounded border text-gray-700">
                            {categories.find(c => c.id === (product.categoryId || product.product?.categoryId || product.category?._id))?.name || 'Unknown Category'}
                        </div>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Select New Category
                        </label>
                        <select
                            value={targetCategoryId}
                            onChange={(e) => setTargetCategoryId(e.target.value)}
                            className="w-full border rounded-md p-2 focus:ring-2 focus:ring-[#C5A57D] focus:border-[#C5A57D] outline-none"
                        >
                            <option value="">-- Select Category --</option>
                            {categories
                                .filter(c => !c.isPending && c.id !== (product.categoryId || product.product?.categoryId || product.category?._id)) // Exclude current & pending
                                .map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))
                            }
                        </select>
                    </div>

                    <div className="flex justify-end space-x-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleMove}
                            disabled={loading || !targetCategoryId}
                            className="px-4 py-2 bg-[#0A5F38] text-white rounded-md hover:bg-[#084e2d] disabled:opacity-50 flex items-center"
                        >
                            {loading ? 'Moving...' : (
                                <>
                                    <Check className="h-4 w-4 mr-1" /> Confirm Move
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MoveProductModal;
